import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { applyMemberHealthCheckFix, MemberHealthCheckIssueResponse } from 'src/shared/services/healthCheckService';

type FixConfirmationDialogProps = {
  open: boolean;
  categoryKey: string;
  issue: MemberHealthCheckIssueResponse;
  onClose: () => void;
  onFixed: () => void;
};

export const FixConfirmationDialog = (props: FixConfirmationDialogProps) => {
  const { open, categoryKey, issue, onClose, onFixed } = props;
  const { t } = useTranslation(['troubleshooting', 'common']);
  const [applying, setApplying] = useState(false);

  const applyFix = () => {
    setApplying(true);
    applyMemberHealthCheckFix(categoryKey, issue.memberId)
      .then(() => {
        toast.success(t('troubleshooting:fix.success') as string);
        onFixed();
      })
      .catch((e) => {
        ConsoleHelper.error(e);
        toast.error(t('troubleshooting:fix.error') as string);
      })
      .finally(() => setApplying(false));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>{t('troubleshooting:fix.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          <Box>
            <Typography variant='body2' color='text.secondary' sx={{ mb: 0.5 }}>
              {t('troubleshooting:fix.member')}
            </Typography>
            <Typography variant='body1' fontWeight={500}>
              {`${issue.firstName ?? ''} ${issue.lastName ?? ''}`.trim()}
              {issue.memberNr ? ` (${issue.memberNr})` : ''}
            </Typography>
          </Box>
          <Box>
            <Typography variant='body2' color='text.secondary' sx={{ mb: 0.5 }}>
              {t('troubleshooting:fix.currentValue')}
            </Typography>
            <Typography variant='body1' sx={{ fontFamily: 'monospace' }}>
              {issue.currentValue ?? '–'}
            </Typography>
          </Box>
          <Box>
            <Typography variant='body2' color='text.secondary' sx={{ mb: 0.5 }}>
              {t('troubleshooting:fix.suggestedValue')}
            </Typography>
            <Typography variant='body1' sx={{ fontFamily: 'monospace', color: 'primary.main', fontWeight: 600 }}>
              {issue.suggestedValue ?? '–'}
            </Typography>
          </Box>
          {issue.description && (
            <Typography variant='body2' color='text.secondary'>
              {issue.description}
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={applying}>
          {t('common:button.cancel')}
        </Button>
        <Button variant='contained' color='primary' onClick={applyFix} disabled={applying} disableElevation>
          {applying ? t('troubleshooting:fix.applying') : t('troubleshooting:fix.apply')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
